import { useCallback, useEffect, useRef, useState } from "react";
import {
    Alert, Box, Button, Collapse, Dialog, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Tab, Tabs, TextField, Typography, useMediaQuery, useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SendIcon from "@mui/icons-material/Send";
import { LoadingButton } from "@mui/lab";
import { openGlobalSnack } from "../utils/snack";
import { formatDateWithMin } from "../../utils/date";
import { CLOSE_REASON_LABEL, HS_AGENT, PRODUCT_LABEL, hsRequest } from "./api";
import { guidance } from './guidance';
import { LeadWork } from "./leadwork";

const MAX_TEXT = 1000;

interface Msg { id: number; from: 'client' | 'company'; type: string; text: string; createdAt: string }

/** Conversa com telefone e mensagem pronta disponíveis em todo atendimento. */
export function LeadChat({ lead, onClose, onChanged }: { lead: any; onClose: () => any; onChanged: () => any }) {
    const [messages, setMessages] = useState<Msg[]>([]);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [closing, setClosing] = useState(false);
    const [closeReason, setCloseReason] = useState('');
    const [closeNote, setCloseNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [section, setSection] = useState(lead.status === 'closed_won' ? 'work' : 'chat');
    const showWork = section !== 'chat';
    const [showContactMessage, setShowContactMessage] = useState(false);
    const [templateMenu, setTemplateMenu] = useState<HTMLElement | null>(null);
    const [menu, setMenu] = useState<HTMLElement | null>(null);
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const nearBottom = useRef(true);
    const [loaded, setLoaded] = useState(false);
    const [returning, setReturning] = useState(false);
    const [botStates, setBotStates] = useState<{ id: string; label: string }[]>([]);
    const [botState, setBotState] = useState('');
    const bottom = useRef<HTMLDivElement>(null);
    // Contato disponível independentemente da janela de envio pelo sistema.
    const [external, setExternal] = useState<{ phone: string; whatsappUrl: string; required: boolean } | null>(null);

    const load = useCallback(async () => {
        try {
            const r = await hsRequest<{ messages: Msg[] }>('GET', `${HS_AGENT}/leads/${lead.id}/messages`);
            setMessages(r.messages); setLoaded(true);
            const d = await hsRequest<{ externalContact: { phone: string; whatsappUrl: string; required: boolean } | null }>('GET', `${HS_AGENT}/leads/${lead.id}`);
            setExternal(d.externalContact || null);
        } catch (e: any) { setError(e.message); }
    }, [lead.id]);

    useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
    useEffect(() => { if (!showWork && nearBottom.current) bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [messages.length, showWork]);

    const contactMessage = `Olá${lead.firstName?.trim() ? ` ${lead.firstName.trim().split(/\s+/)[0]}` : ''}, você gostaria de ajuda para finalizar a sua proposta?`;

    async function copyContactMessage() {
        try {
            await navigator.clipboard.writeText(contactMessage);
            openGlobalSnack('Mensagem copiada.', 'success');
        } catch {
            setShowContactMessage(true);
            setError('Não foi possível copiar. Selecione e copie o texto da mensagem exibida.');
        }
    }

    async function markExternalDone() {
        setBusy(true); setError('');
        try { await hsRequest('POST', `${HS_AGENT}/leads/${lead.id}/external-contact`); openGlobalSnack('Contato registrado.', 'success'); await load(); onChanged(); }
        catch (e: any) { setError(e.message); }
        finally { setBusy(false); }
    }

    async function send() {
        const value = text.trim();
        if (!value || sending) return;
        setSending(true); setError('');
        try {
            await hsRequest('POST', `${HS_AGENT}/leads/${lead.id}/send`, { text: value });
            setText(''); nearBottom.current = true;
            await load();
            onChanged();
        } catch (e: any) { setError(e.message); }
        setSending(false);
    }
    async function release() {
        if (!window.confirm('Devolver este lead para a fila? Outro atendente poderá pegá-lo.')) return;
        setBusy(true);
        try { await hsRequest('POST', `${HS_AGENT}/leads/${lead.id}/release`); openGlobalSnack('Lead devolvido à fila.', 'success'); onChanged(); onClose(); }
        catch (e: any) { setError(e.message); }
        setBusy(false);
    }
    async function openReturn() {
        setError('');
        try {
            const r = await hsRequest<{ states: { id: string; label: string }[]; stoppedState: string | null }>('GET', `${HS_AGENT}/leads/${lead.id}/bot-states`);
            setBotStates(r.states); setBotState(r.states.find((x) => x.id === r.stoppedState)?.id || r.states[0]?.id || ''); setReturning(true);
        } catch (e: any) { setError(e.message); }
    }
    async function returnToBot() {
        if (!botState) { setError('Escolha o estado.'); return; }
        if (!window.confirm('Desligar o atendimento e devolver este cliente ao robô? Você deixa de conversar com ele; sua comissão segue valendo se o robô fechar a venda.')) return;
        setBusy(true);
        try {
            await hsRequest('POST', `${HS_AGENT}/leads/${lead.id}/return-to-bot`, { state: botState });
            openGlobalSnack('Atendimento encerrado: o robô assume do estado escolhido.', 'success'); onChanged(); onClose();
        } catch (e: any) { setError(e.message); }
        setBusy(false);
    }
    async function closeLost() {
        if (!closeReason) { setError('Escolha o motivo.'); return; }
        setBusy(true);
        try {
            await hsRequest('POST', `${HS_AGENT}/leads/${lead.id}/close`, { reason: closeReason, note: closeNote });
            openGlobalSnack('Lead encerrado como perdido.', 'success'); onChanged(); onClose();
        } catch (e: any) { setError(e.message); }
        setBusy(false);
    }

    return (
        <Dialog open fullWidth fullScreen={mobile} maxWidth="md" onClose={busy || sending ? undefined : onClose}
            PaperProps={{ className: 'sales-workspace', sx: { height: mobile ? '100dvh' : 'min(900px, 92dvh)', maxHeight: '100dvh', m: mobile ? 0 : 2, borderRadius: mobile ? 0 : 3, overflow: 'hidden' } }}>
            <DialogTitle sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton aria-label="Voltar para meus clientes" disabled={busy || sending} onClick={onClose}><ArrowBackIcon /></IconButton>
                    <Box sx={{ flex: 1, minWidth: 0 }}><Typography component="h2" fontWeight={700} noWrap>{lead.firstName || 'Cliente'}</Typography><Typography variant="caption" color="text.secondary">{PRODUCT_LABEL[lead.product] || lead.product}</Typography></Box>
                    <IconButton aria-label="Mais ações do atendimento" aria-haspopup="menu" onClick={(e) => setMenu(e.currentTarget)} disabled={busy || sending || lead.status === 'closed_won'}><MoreVertIcon /></IconButton>
                </Box>
                <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
                    <MenuItem onClick={() => { setMenu(null); setClosing(false); openReturn(); }}>Devolver ao robô</MenuItem>
                    <MenuItem onClick={() => { setMenu(null); release(); }}>Devolver à fila</MenuItem>
                    <MenuItem sx={{ color: 'error.main' }} onClick={() => { setMenu(null); setReturning(false); setClosing(true); }}>Encerrar sem venda</MenuItem>
                </Menu>
            </DialogTitle>
            {external ? (
                <Box sx={{ px: 2, pb: 1.5, flexShrink: 0 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ userSelect: 'text' }}>{external.phone}</Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        <Button size="small" variant="outlined" title={contactMessage} onClick={copyContactMessage}>Copiar mensagem pronta</Button>
                        <Button size="small" variant={external.required && !showWork ? "contained" : "outlined"} component="a" href={`${external.whatsappUrl}?text=${encodeURIComponent(contactMessage)}`} target="_blank" rel="noopener noreferrer">Abrir no WhatsApp</Button>
                        <LoadingButton size="small" loading={busy} onClick={markExternalDone}>Já chamei o cliente</LoadingButton>
                    </Box>
                    <Collapse in={showContactMessage}><Typography variant="body2" sx={{ userSelect: 'text', py: 1 }}>{contactMessage}</Typography></Collapse>
                    {external.required ? <Typography variant="caption" color="text.secondary">Continue o atendimento pelo botão Abrir no WhatsApp e registre o contato após chamar o cliente.</Typography> : null}
                </Box>
            ) : null}
            <Tabs value={section} onChange={(_, v) => setSection(v)} variant="fullWidth" aria-label="Área do cliente" sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Tab value="chat" label="Conversa" /><Tab value="work" label="Proposta" />
            </Tabs>
            <Box sx={{ display: section === 'work' ? 'block' : 'none', flex: 1, minHeight: 0, overflowY: 'auto' }}><LeadWork lead={lead} /></Box>
            <DialogContent dividers onScroll={(e) => { const el = e.currentTarget; nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100; }} sx={{ display: showWork ? 'none' : 'block', flex: 1, minHeight: 0, p: { xs: 2, sm: 3 }, background: '#f1f5f9' }}>
                <Alert severity="info" sx={{ mb: 2 }} action={lead.reason === 'formalization' || lead.reason === 'pendency' ? <Button color="inherit" onClick={() => setSection('work')}>Ver proposta</Button> : undefined}>{lead.nextAction || guidance(lead).action}</Alert>
                {messages.length === 0 ? <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>{loaded ? (external?.required ? 'Sem mensagens ainda. Chame o cliente pelo WhatsApp.' : 'Sem mensagens ainda. Envie a primeira abaixo.') : 'Carregando conversa…'}</Typography> : null}
                {messages.map((m) => (
                    <Box key={m.id} sx={{ display: 'flex', justifyContent: m.from === 'company' ? 'flex-end' : 'flex-start', mb: 1 }}>
                        <Box sx={{ maxWidth: '88%', px: 1.5, py: 1, borderRadius: 2, background: m.from === 'company' ? '#dceee9' : '#fff', boxShadow: 1 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatDateWithMin(m.createdAt)}</Typography>
                        </Box>
                    </Box>
                ))}
                <div ref={bottom} />
            </DialogContent>
            {error ? <Alert severity="error" onClose={() => setError('')} sx={{ m: 1 }}>{error}</Alert> : null}
            {returning ? (
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2">O robô volta a atender este cliente a partir do estado escolhido (sem enviar mensagem agora; ele retoma quando o cliente responder).</Typography>
                    <TextField select size="small" label="Estado em que o robô retoma" value={botState} onChange={(e) => setBotState(e.target.value)}>
                        {botStates.map((x) => <MenuItem key={x.id} value={x.id}>{x.label}</MenuItem>)}
                    </TextField>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button onClick={() => setReturning(false)}>Voltar</Button>
                        <LoadingButton variant="contained" loading={busy} onClick={returnToBot}>Devolver ao robô</LoadingButton>
                    </Box>
                </Box>
            ) : closing ? (
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField select size="small" label="Motivo" value={closeReason} onChange={(e) => setCloseReason(e.target.value)}>
                        {Object.entries(CLOSE_REASON_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                    </TextField>
                    <TextField size="small" label="Observação (opcional)" value={closeNote} inputProps={{ maxLength: 500 }} onChange={(e) => setCloseNote(e.target.value)} />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button onClick={() => setClosing(false)}>Voltar</Button>
                        <LoadingButton color="error" variant="contained" loading={busy} onClick={closeLost}>Encerrar como perdido</LoadingButton>
                    </Box>
                </Box>
            ) : !showWork && !external?.required ? (
                <Box sx={{ p: 2, pb: 'max(16px, env(safe-area-inset-bottom))', flexShrink: 0 }}>
                    <Button size="small" disabled={sending} onClick={(e) => setTemplateMenu(e.currentTarget)}>Mensagens de apoio</Button>
                    <Menu anchorEl={templateMenu} open={Boolean(templateMenu)} onClose={() => setTemplateMenu(null)}>
                        {guidance(lead).messages.map((suggestion) => <MenuItem key={suggestion.label} onClick={() => {
                            const draft = text ? `${text}\n\n${suggestion.text}` : suggestion.text;
                            if (draft.length > MAX_TEXT) setError('A mensagem ficaria longa demais. Reduza o rascunho antes de inserir a sugestão.');
                            else setText(draft);
                            setTemplateMenu(null);
                        }}>{suggestion.label}</MenuItem>)}
                    </Menu>
                    <TextField fullWidth multiline minRows={1} maxRows={4} size="small" placeholder="Escreva sua mensagem…" value={text}
                        disabled={sending} inputProps={{ maxLength: MAX_TEXT, 'aria-label': 'Mensagem para o cliente' }} onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) send(); }}
                        helperText="Não envie telefone, @, e-mail ou links de contato." />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">{text.length}/{MAX_TEXT}{!mobile ? ' • Ctrl+Enter envia' : ''}</Typography>
                        <LoadingButton variant="contained" endIcon={<SendIcon />} loading={sending} disabled={!text.trim()} onClick={send}>Enviar</LoadingButton>
                    </Box>
                </Box>
            ) : null}

        </Dialog>
    );
}
